import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { saldoService } from "../services/saldo-service";
import { Cadastro, SearchFormData } from "../types";
import {
  cleanCnpj,
  formatCnpj,
  formatCpf,
  formatPhone,
  isValidCnpj,
  isValidCpf,
  isValidPhone,
} from "../utils/format";

export const saldoConsultaKeys = {
  all: ["saldo-consulta"] as const,
  search: (fundName: string, searchParams: string) =>
    [...saldoConsultaKeys.all, "search", fundName, searchParams] as const,
};

interface UseSaldoConsultaProps {
  fundName: string;
}

export interface FieldErrors {
  telefone?: string;
  cpf?: string;
  cnpj?: string;
}

export function useSaldoConsulta({ fundName }: UseSaldoConsultaProps) {
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    telefone: "",
    cpf: "",
    cnpj: "",
  });
  const [searchResults, setSearchResults] = useState<Cadastro[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const searchMutation = useMutation({
    mutationFn: async (searchData: SearchFormData) => {
      const params: any = { dbName: fundName };

      if (searchData.telefone.trim()) {
        params.telefone = searchData.telefone.replace(/\D/g, "");
      }
      if (searchData.cpf.trim()) {
        params.cpf = searchData.cpf.replace(/\D/g, "");
      }
      if (searchData.cnpj.trim()) {
        params.cnpj = cleanCnpj(searchData.cnpj);
      }

      return saldoService.searchSaldo(params);
    },
    onSuccess: (response) => {
      let registrosList: Cadastro[] | null = null;

      if (response?.data?.data?.registros) {
        registrosList = response.data.data.registros;
      } else if (response?.data?.registros) {
        registrosList = response.data.registros;
      }

      setSearchResults(
        registrosList && registrosList.length > 0 ? registrosList : null
      );
    },
    onError: () => {
      setSearchResults(null);
    },
  });

  const validateField = (name: keyof SearchFormData, value: string) => {
    const errors = { ...fieldErrors };

    switch (name) {
      case "telefone":
        if (value && !isValidPhone(value)) {
          errors.telefone = "Telefone deve ter 10-11 dígitos (DDD + número)";
        } else {
          delete errors.telefone;
        }
        break;
      case "cpf":
        if (value && !isValidCpf(value)) {
          errors.cpf = "CPF deve ter 11 dígitos";
        } else {
          delete errors.cpf;
        }
        break;
      case "cnpj":
        if (value && !isValidCnpj(value)) {
          errors.cnpj = "CNPJ deve ter 14 dígitos válidos";
        } else {
          delete errors.cnpj;
        }
        break;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: keyof SearchFormData, value: string) => {
    let formattedValue = value;

    if (field === "cpf") {
      formattedValue = formatCpf(value);
    } else if (field === "cnpj") {
      formattedValue = formatCnpj(value);
    } else if (field === "telefone") {
      formattedValue = formatPhone(value);
    }

    setSearchForm((prev) => ({ ...prev, [field]: formattedValue }));

    setTimeout(() => {
      validateField(field, formattedValue);
    }, 300);
  };

  const validateSearchForm = (): { valid: boolean; message?: string } => {
    const { telefone, cpf, cnpj } = searchForm;

    if (!telefone && !cpf && !cnpj) {
      return {
        valid: false,
        message: "Preencha pelo menos um campo: telefone, CPF ou CNPJ",
      };
    }

    if (telefone && !isValidPhone(telefone)) return { valid: false };
    if (cpf && !isValidCpf(cpf)) return { valid: false };
    if (cnpj && !isValidCnpj(cnpj)) return { valid: false };

    if (Object.keys(fieldErrors).length > 0) {
      return {
        valid: false,
        message: "Corrija os erros nos campos antes de continuar",
      };
    }

    return { valid: true };
  };

  const handleSearch = () => {
    const validation = validateSearchForm();
    if (!validation.valid) {
      return validation;
    }

    searchMutation.mutate(searchForm);
    return { valid: true };
  };

  return {
    searchForm,
    searchResults,
    fieldErrors,
    handleFieldChange,
    handleSearch,
    isSearching: searchMutation.isPending,
    searchError: searchMutation.error,
  };
}
